import { motion } from "framer-motion";
import { X } from "lucide-react";
import React, { useRef } from "react";
import Draggable from "react-draggable";
import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";
import { useHapticEngine } from "../../hooks/useHapticsEngine";
import { useScrollLock } from "../../hooks/useScrollLock";
import { useLocalLibraryStore } from "../../stores/localLibraryStore";
import { usePlayerStore } from "../../stores/playerStore";
import { getDefaultAlbumArt } from "../../utils/defaults";
import { ExpandedPlayer } from "./ExpandedPlayer";

export const WindowedPlayer = () => {
  const {
    currentTrack,
    isPlaying,
    progress,
    volume,
    loopMode,
    windowedPosition,
    windowedSize,
    setWindowedPosition,
    setWindowedSize,
    setMode,
    togglePlay,
    playNext,
    playPrevious,
    setLoopMode,
    setVolume,
  } = usePlayerStore();

  const { isContentLiked, toggleLike } = useLocalLibraryStore();
  const {
    settings: hapticsSettings,
    updateSettings: updateHapticsSettings,
    isSupported: isHapticsSupported,
  } = useHapticEngine(new AudioContext());
  const nodeRef = useRef<HTMLDivElement>(null);

  // Lock scroll when windowed player is active
  useScrollLock(true);

  if (!currentTrack) return null;

  const albumArt = currentTrack.albumArt || getDefaultAlbumArt(currentTrack.source);

  const handleDrag = (_e: any, data: { x: number; y: number }) => {
    setWindowedPosition({ x: data.x, y: data.y });
  };

  const handleResize = (_e: React.SyntheticEvent, { size }: { size: { width: number; height: number } }) => {
    setWindowedSize({
      width: Math.max(300, Math.min(size.width, window.innerWidth)),
      height: Math.max(400, Math.min(size.height, window.innerHeight)),
    });
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: currentTrack.title,
        text: `Check out ${currentTrack.title} by ${currentTrack.artist}`,
        url: window.location.href,
      });
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  const handleHapticsToggle = () => {
    updateHapticsSettings({ enabled: !hapticsSettings.enabled });
  };

  return (
    <div className="fixed inset-0 z-50">
      <Draggable
        nodeRef={nodeRef}
        handle=".window-handle"
        position={windowedPosition}
        onDrag={handleDrag}
        bounds="body"
      >
        <div
          ref={nodeRef}
          className="absolute"
          style={{
            touchAction: "none",
            transform: `translate(${windowedPosition.x}px, ${windowedPosition.y}px)`,
          }}
        >
          <Resizable
            width={windowedSize.width}
            height={windowedSize.height}
            minConstraints={[300, 400]}
            maxConstraints={[window.innerWidth, window.innerHeight]}
            onResize={handleResize}
            resizeHandles={["se"]}
            handle={
              <div className="absolute bottom-0 right-0 w-6 h-6 cursor-se-resize group">
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-zinc-600 group-hover:bg-yellow-500 transition-colors transform rotate-45" />
              </div>
            }
          >
            <motion.div
              className="bg-zinc-900/95 backdrop-blur-lg rounded-lg shadow-2xl overflow-hidden border border-zinc-800/50"
              style={{
                width: `${windowedSize.width}px`,
                height: `${windowedSize.height}px`,
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
              }}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <div className="window-handle relative flex items-center justify-between p-4 bg-black/50 cursor-move select-none">
                <h3 className="text-lg font-semibold">Now Playing</h3>
                <button
                  onClick={() => setMode("mini")}
                  className="p-1.5 hover:bg-zinc-700/50 rounded-full transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="h-[calc(100%-4rem)] overflow-y-auto">
                <ExpandedPlayer
                  title={currentTrack.title}
                  artist={currentTrack.artist}
                  albumArt={albumArt}
                  isPlaying={isPlaying}
                  progress={progress}
                  volume={volume}
                  duration={currentTrack.duration}
                  loopMode={loopMode}
                  isLiked={isContentLiked(currentTrack.id, "track")}
                  isHapticsSupported={isHapticsSupported}
                  isHapticsEnabled={hapticsSettings.enabled}
                  hideHeader
                  isDragging={false}
                  dragProgress={null}
                  onMinimize={() => setMode("mini")}
                  onPlayPause={togglePlay}
                  onNext={playNext}
                  onPrevious={playPrevious}
                  onLoopChange={setLoopMode}
                  onVolumeChange={setVolume}
                  onLikeToggle={() => toggleLike(currentTrack.id, "track", currentTrack.source)}
                  onShare={handleShare}
                  onHapticsToggle={handleHapticsToggle}
                  onSeekStart={() => {}}
                  onSeek={() => {}}
                  onSeekEnd={() => {}}
                  onAddToPlaylist={() => {}}
                  onAddToAlbum={() => {}}
                />
              </div>
            </motion.div>
          </Resizable>
        </div>
      </Draggable>
    </div>
  );
};
